import type { Language } from '@vmc/shared';
import {
  createContext,
  use,
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate } from 'react-router';

/**
 * Per-user UI preferences (DESIGN.md rule 8): content and defaults adapt, structure never does.
 * Stored in localStorage under the user's id, so two staff sharing a PC keep their own settings.
 * Every access is guarded: private mode or blocked storage just means preferences don't persist.
 */
export interface Recent {
  kind: 'citizen' | 'chat' | 'kb';
  id: string;
  label: string;
  sub?: string;
}

export interface Prefs {
  density: 'compact' | 'comfortable';
  kbTestLang: Language;
  chatTab: string;
  /** Last query string per list page ("citizens" → "wardIds=…&tags=…"). */
  filters: Record<string, string>;
  recents: Recent[];
}

const DEFAULTS: Prefs = {
  density: 'compact',
  kbTestLang: 'EN',
  chatTab: 'queued',
  filters: {},
  recents: [],
};
const MAX_RECENTS = 8;

function createStore(userId: string) {
  const key = `vmc:prefs:${userId}`;
  let state: Prefs = DEFAULTS;
  try {
    const raw = localStorage.getItem(key);
    if (raw) state = { ...DEFAULTS, ...(JSON.parse(raw) as Partial<Prefs>) };
  } catch {
    // unreadable → defaults
  }
  const listeners = new Set<() => void>();
  return {
    get: () => state,
    subscribe: (l: () => void) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    set: (patch: Partial<Prefs>) => {
      state = { ...state, ...patch };
      try {
        localStorage.setItem(key, JSON.stringify(state));
      } catch {
        // quota / private mode: keep in memory only
      }
      for (const l of listeners) l();
    },
  };
}

type Store = ReturnType<typeof createStore>;
const PrefsContext = createContext<Store | null>(null);

export function PrefsProvider({ userId, children }: { userId: string; children: ReactNode }) {
  const [store] = useState(() => createStore(userId));
  const density = useSyncExternalStore(store.subscribe, () => store.get().density);

  // Density is a token switch on <html> (tokens.css), so every table follows it without props.
  useEffect(() => {
    document.documentElement.dataset.density = density;
  }, [density]);

  return <PrefsContext value={store}>{children}</PrefsContext>;
}

function useStore(): Store {
  const s = use(PrefsContext);
  if (!s) throw new Error('usePref must be used inside <PrefsProvider>');
  return s;
}

export function usePref<K extends keyof Prefs>(key: K): [Prefs[K], (value: Prefs[K]) => void] {
  const store = useStore();
  const value = useSyncExternalStore(store.subscribe, () => store.get()[key]);
  const set = useCallback((v: Prefs[K]) => store.set({ [key]: v }), [store, key]);
  return [value, set];
}

/** Recently opened citizens/chats for the command palette (most recent first, deduplicated). */
export function useRecents(): [Recent[], (r: Recent) => void] {
  const store = useStore();
  const recents = useSyncExternalStore(store.subscribe, () => store.get().recents);
  const add = useCallback(
    (r: Recent) => {
      const rest = store.get().recents.filter((x) => !(x.kind === r.kind && x.id === r.id));
      store.set({ recents: [r, ...rest].slice(0, MAX_RECENTS) });
    },
    [store],
  );
  return [recents, add];
}

/**
 * Remembers a list page's filters (its query string) and restores them when the page is opened
 * without any, e.g. from the sidebar. Explicit links with a query always win.
 */
export function useRememberedFilters(page: string, ignore: string[] = []): void {
  const store = useStore();
  const { search, pathname } = useLocation();
  const navigate = useNavigate();
  const restored = useRef(false);
  const ignoreRef = useRef(ignore);

  useEffect(() => {
    if (!restored.current) {
      restored.current = true;
      const saved = store.get().filters[page];
      if (!search && saved) {
        void navigate({ pathname, search: `?${saved}` }, { replace: true });
        return;
      }
    }
    const params = new URLSearchParams(search);
    for (const k of ignoreRef.current) params.delete(k); // transient state (open drawer) isn't a filter
    store.set({ filters: { ...store.get().filters, [page]: params.toString() } });
  }, [search, pathname, page, navigate, store]);
}
